// 局部替换
import { INTELLIGENT_EDIT_V2_MODEL_ID, ModuleEnum } from '../moduleEnum';
import type { LayoutReferState } from '@/types/image';
import { getModelEventTrackType } from '../index';
import { $bus } from '@/mitt';

export const module = ModuleEnum['replace'];

// 生图参数
export const replaceParams = {
  module,
  prompt: '',
  negativePrompt: '',
  // batchSize: defaultBatchSize,
  batchSize: 1,
  layoutRefer: null as null | LayoutReferState, // 原始稿件
  maskImage: '',
  maskContrastImage: null as LayoutReferState | null,
  styleReferList: [] as LayoutReferState[],
  originStyleRefer: null as null | LayoutReferState,
  showOriginImage: false, // 查看原图
  smearType: 1, // 1: 替换 2: 擦除
}

import { useArtworkStore } from '@/store/artwork';
import { useUserStore } from '@/store/user';
import { filteringInvalidFields } from '@/utils';

// 将当前用户输入的生图参数转换成后端所需要
function getReplaceGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const module = replaceParams.module;

  let params = {
    module,
    modelList: artworkStore.artworkState.modelList,
    loraList: artworkStore.artworkState.modelList.map(item => {
      return {
        modelId: item.id,
        strength: item.strength
      }
    }),
    source: 1,
    smearType: artworkStore.replace.params.smearType,
    historyJsonUrl: artworkStore.replace.params.historyJsonUrl || '',
    userId: userStore.userInfo.id,
    uniqueId: userStore.userInfo.uniqueId,
    artworkId: artworkStore.artworkState.id,
    layoutRefer: artworkStore.replace.params.layoutRefer,
    layoutReferId: artworkStore.replace.params.layoutRefer?.id || null,
    maskImage: artworkStore.replace.params.maskContrastImage,
  } as any;


  params.maskId = artworkStore.replace.params.maskContrastImage?.id || null;
  params.batchSize = artworkStore.replace.params.batchSize;
  params.prompt = artworkStore.replace.params.prompt;
  params.negativePrompt = artworkStore.replace.params.negativePrompt;

  // params.styleReferList = editModuleStore.replace.styleReferList;
  params.styleReferList = [];
  // params.commonReferId = params.styleReferList.length > 0 ? params.styleReferList[0].id : null;
  params.commonReferId = null;

  // 去除 layoutRefer 中的无效字段
  filteringInvalidFields(params.layoutRefer, ['id', 'width', 'height', 'image']);

  // 是否使用加速通道
  params.vipChannel = artworkStore.channel === 'expedite';

  // 全量使用V2版本工作流
  params.module = INTELLIGENT_EDIT_V2_MODEL_ID;
  params.loraList = [];

  return params;
}

import { useEventTracking } from '@/hooks/useEventTracking';
import type { ModelState } from '@/types/model';
import { startModuleGenerate } from './index';
import { useModelStore } from '@/store/model';
import { styleModelOptions } from '@/constants';


// 开启模块生图
export async function startReplaceGenerate(oldParams?: any, options?: {
  onClientIdGenerated?: (clientId: string) => void;
  onClientIdCleared?: () => void;
}) {
  const artworkStore = useArtworkStore();
  const modelStore = useModelStore();
  const params = await getReplaceGenerateParam();
  const module = replaceParams.module;
  const modelList = artworkStore.artworkState.modelList || [];
  const modelLabels = modelList.flatMap(item => item.label || []);
  // 按照 modelLabels 中 ID 的顺序过滤和排序标签
  const modelTagList = modelLabels
    .map((labelId) => modelStore.labelOptions.find((item: any) => item.value === labelId))
    .filter(Boolean)
    .map((item) => item!.label);

  const modelCategories = modelList.map((item) => item.category).filter(Boolean);

  const modelTypeList = modelCategories
    .map((categoryId) => modelStore.categoryOptions.find((item: any) => item.value === categoryId))
    .filter(Boolean)
    .map((item) => item!.label);

  const isUserGeneratedText = (() => {
    if (modelList.every(i => i.userId !== 0)) return '是';
    if (modelList.every(i => i.userId === 0)) return '否';
    return '混合';
  })();
  const modelIdList = params.modelList.map((item: ModelState) => item.id);
  const modelNameList = modelList.map((i: any) => i.modelName) || [];
  // 创建loraType映射关系
  const loraTypeMap = Object.fromEntries(styleModelOptions.map((option) => [option.value, option.label]));
  // 提取loraType对应的label组成数组
  const loraTypeLabels = modelList.map((item) => item.loraType ? loraTypeMap[item.loraType] || '视频模型' : item.loraType);

  startModuleGenerate(
    module,
    {
      getModuleParams: () => {
        return oldParams || params;
      },
      onStartTrack: (clientId) => {
        reportStartEventTracking(clientId);
      },
      onFinishTrack: (data) => {
        reportFinishEventTracking(data);
      },
      onClientIdGenerated: options?.onClientIdGenerated,
      onClientIdCleared: options?.onClientIdCleared,
    }
  );

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {

    const eventValue: any = {
      client_id: clientId,
      module: params.smearType === 1 ? '涂抹替换-替换' : '涂抹替换-擦除',
      source: params.source === 1 ? '直接生成' : '再迭代',
      artwork_id: artworkStore.artworkState.id.toString(),
      prompt: params.prompt,
      negative_prompt: params.negativePrompt,
      face_detail: params.faceDetail ? 1 : 0,
      simplebg: params.simpleBackground ? 1 : 0,
      style_refer: params.styleReferList?.length > 0 ? '1' : '0',
      batchSize: params.batchSize,
      // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
      hp_model_id: String(modelIdList.join(',')),
      model_name: params.modelList.map((item: ModelState) => item.modelName).join(',') || JSON.stringify(params.modelList || ['--']),
      ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(','),
      lora_type: params.modelList.map((item: ModelState) => item.loraType).join(','),
      is_personal_model: getModelEventTrackType(params.modelList),
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      model_tags: modelTagList.join(','),
      model_type: modelTypeList.join(','),

      hp_model_id_list: modelIdList,
      hp_model_name_list: modelNameList,
      hp_model_tags_list: modelTagList,
      hp_model_type_list: modelTypeList,
      hp_is_user_generated: isUserGeneratedText,
      is_composite_model: loraTypeLabels
    }

    useEventTracking({
      event: 'creation_event',
      eventValue
    });
  }

  function reportFinishEventTracking({
    client_id = '',
    result = 0 as 0 | 1,
    imageIds = '',
    queue_time_number = 0,
    loading_time_number = 0
  } = {}) {
    useEventTracking({
      event: 'artwork_status_event',
      eventValue: {
        module: params.smearType === 1 ? '涂抹替换-替换' : '涂抹替换-擦除',
        client_id,
        source: params.source === 1 ? '直接生成' : '再迭代',
        img_id: imageIds,
        result,
        queue_time_number,
        loading_time_number,
        // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
        hp_model_id: String(modelIdList.join(',')),
        hp_model_id_list: modelIdList,
        model_name: params.modelList.map((item: ModelState) => item.modelName).join(',') || JSON.stringify(params.modelList || ['--']),
        ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(','),
        lora_type: params.modelList.map((item: ModelState) => item.loraType).join(','),
        is_personal_model: getModelEventTrackType(params.modelList),
        is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
        prompt: params.prompt,
        negative_prompt: params.negativePrompt,
        face_detail: params.faceDetail ? 1 : 0,
        simplebg: params.simpleBackground ? 1 : 0,
        style_refer: params.styleReferList?.length > 0 ? '1' : '0',
        model_tags: modelTagList.join(','),
        hp_model_tags_list: modelTagList,
        model_type: modelTypeList.join(','),
        hp_model_type_list: modelTypeList,
        hp_is_user_generated: isUserGeneratedText
      }
    });

  }
}